import { useCallback, useEffect, useRef, useState } from "react";
import { FaArrowLeft, FaArrowRight, FaCheckCircle, FaFastBackward, FaFastForward } from "react-icons/fa";
import { TbCircleXFilled } from "react-icons/tb";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { axiosServer } from "../../../../axiosServer";
import buildLink from "../../../../urls";
import axios from "axios";
import MessageAlert from "../../../../components/messageAlert";
import { PlaySounds } from "../../../../functions/playSounds";
import hocStarredPage from "../../../../components/hocStarredPage";
import PointsLoader from "../../../../components/PointsLoader";

const ScanPickUpFloor = () => {
  const [usersIds, setUsersIds] = useState([]);
  const hiddenProductId = useRef(null);
  const { id } = useParams();
  const history = useNavigate();
  const [hasproductFocus, setHasproductFocus] = useState(false);
  const [hasbinFocus, setHasBinFocus] = useState(true);
  const { playErrorSound, playSuccessSound } = PlaySounds();
  const [list, setList] = useState();
  const [floorProducts, setFloorProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scannedValue = useRef();
  const [showMessgSucc, setShowMessageSucc] = useState(false);
  const [message, setMessage] = useState("");
  const [showMessgErr, setShowMessageErr] = useState(false);
  const [storeType, setStoreType] = useState(null);
  const [storeTypeVal, setStoreTypeVal] = useState(null);
  const location = useLocation();
  const [next, setNext] = useState(false);
  const [missing, setMissing] = useState(false);
  const [disable, setDisable] = useState(false);
  const [missingButtonHidden, setMissingButtonHidden] = useState(false);
  const [productsPicked, setProductsPicked] = useState(0);
  const [productsNotPicked, setProductsNotPicked] = useState(0);
  const [indexProduct, setindexProduct] = useState(0);
  const [counter, setCounter] = useState(0);
  const [localScannedCounts, setLocalScannedCounts] = useState({});
  const [isScannedBin, setIsScannedBin] = useState(false);
  const biggerThanTen = useRef();

  // Function to get query parameters
  const getQueryParams = (param) => {
    return new URLSearchParams(location.search).get(param);
  };

  const getUserIdFromLocalStorage = () => {
    return localStorage.getItem("userID");
  };

  // Helper function to check if a product is considered "complete" (picked, missing, or marked as missing)
  const isProductComplete = (product) => {
    return product.picked || product.missing_submitted || product.has_missing;
  };

  // Helper function to find the next unscanned product
  const findNextUnscannedProduct = useCallback((products, currentIndex) => {
    // First, look for unscanned products after the current index
    for (let i = currentIndex + 1; i < products.length; i++) {
      if (!isProductComplete(products[i])) {
        return i;
      }
    }
    
    // If no unscanned products found after current index, look from the beginning
    for (let i = 0; i <= currentIndex; i++) {
      if (!isProductComplete(products[i])) {
        return i;
      }
    }
    
    // If no unscanned products found, return -1 (all products are complete)
    return -1;
  }, []);

  // Navigate to next unscanned product or finish
  const navigateToNextProduct = useCallback((products, currentIndex) => {
    const nextIndex = findNextUnscannedProduct(products, currentIndex);
    if (nextIndex !== -1) {
      setindexProduct(nextIndex);
      // Reset focus to bin scanner
      setTimeout(() => {
        scannedValue.current?.focus();
      }, 100);
    } else {
      // All products are complete, go back
      localStorage.removeItem("pickupFloorRequestInfo");
      history("/home/v2home/orderpage/pickupfloor/");
    }
  }, [findNextUnscannedProduct, history]);

  useEffect(() => {
    if (floorProducts) {
      let numberProductPicked = 0;
      let numberProductNotPicked = 0;
      floorProducts.forEach((product) => {
        if (product.picked) {
          numberProductPicked += Number(product.quantity);
        } else {
          numberProductNotPicked += Number(product.quantity);
        }
      });
      setProductsPicked(numberProductPicked);
      setProductsNotPicked(numberProductNotPicked);
    }
  }, [floorProducts]);

  // Store last requestInfo for later use
  const lastRequestInfoRef = useRef(null);
  const runRequests = async (requestInfo) => {
    try {
      setLoading(true);
      const { userID, floor_id, orders_quantity, returns_quantity } = requestInfo;
      // Loop for orders
      for (let i = 0; i < orders_quantity; i++) {
        const orderUrl = buildLink("getFloorProducts") + `&user_id=${userID}&floor_id=${floor_id}&page=${i + 1}&type=order`;
        await axiosServer.get(orderUrl);
      }
      // Loop for returns
      for (let i = 0; i < returns_quantity; i++) {
        const returnUrl = buildLink("getFloorProducts") + `&user_id=${userID}&floor_id=${floor_id}&page=${i + 1}&type=return`;
        await axiosServer.get(returnUrl);
      }
      // Final sorting request
      const sortingUrl = buildLink("sortingFloorProducts") + `&user_id=${userID}&floor_id=${floor_id}`;
      const sortingResponse = await axiosServer.get(sortingUrl);
      const products = sortingResponse.data.data.order_products || [];
      setFloorProducts(products);
      setList(sortingResponse.data.data.list);
      setUsersIds(sortingResponse.data.data.stand_bypass_users || []);
      
      // After loading products, find the first unscanned product
      if (products.length > 0) {
        const firstUnscanned = findNextUnscannedProduct(products, -1);
        if (firstUnscanned !== -1) {
          setindexProduct(firstUnscanned);
        }
      }
    } catch (e) {
      setMessage("Failed to fetch floor products. Please try again.");
      setShowMessageErr(true);
      setTimeout(() => setShowMessageErr(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Try to get from localStorage
    const requestInfoStr = localStorage.getItem("pickupFloorRequestInfo");
    if (requestInfoStr) {
      const requestInfo = JSON.parse(requestInfoStr);
      lastRequestInfoRef.current = requestInfo;
      runRequests(requestInfo);
    } else {
      setLoading(false);
    }
  }, [location.state]);

  useEffect(() => {
    if (floorProducts && floorProducts[indexProduct]) {
      const productId = floorProducts[indexProduct].order_product_id;
      const localCount = localScannedCounts[productId] || 0;
      const serverCount = floorProducts[indexProduct].picked_qty ? parseInt(floorProducts[indexProduct].picked_qty, 10) : 0;
      setCounter(Math.max(localCount, serverCount));
      setMissing(floorProducts[indexProduct].has_missing);
      if (floorProducts[indexProduct].bin_id) {
        setStoreType('Bin');
        setStoreTypeVal(floorProducts[indexProduct].floor_name);
      } else if (floorProducts[indexProduct].upc) {
        setStoreType('Upc');
        setStoreTypeVal(floorProducts[indexProduct].upc);
      } else if (floorProducts[indexProduct].big_upc) {
        setStoreType("BigUpc");
        setStoreTypeVal(floorProducts[indexProduct].big_upc);
      }
    }
  }, [indexProduct, floorProducts]);

  useEffect(() =>{
    scannedValue.current.value = '';
    scannedValue.current.focus();
    setIsScannedBin(false);
  },[indexProduct])

  async function checkShelfLabel(e) {
    e.preventDefault();
    if (scannedValue.current.value !== '' && (scannedValue.current.value == floorProducts[indexProduct].bin_id || scannedValue.current.value == floorProducts[indexProduct].upc || scannedValue.current.value == floorProducts[indexProduct].big_upc || counter == floorProducts.quantity)) {
      setIsScannedBin(true);
      setMessage(`Right ${storeType} for Product! Scan Product to pick it up.`);
      playSuccessSound();
      setShowMessageSucc(true);
      hiddenProductId.current.focus();
      setTimeout(() => {
        setShowMessageSucc(false);
      }, 3000);
    } else {
      setIsScannedBin(false);
      scannedValue.current.value = "";
      setMessage("Bin or Upc or BigUpc Not Correct ! ");
      playErrorSound();
      setShowMessageErr(true);
      setTimeout(() => {
        setShowMessageErr(false);
      }, 3000);
    }
    hiddenProductId.current.value = "";
  }

  async function scanProduct(e) {
    e.preventDefault();

    if(scannedValue.current.value == "" && (scannedValue.current.value !== floorProducts[indexProduct].bin_id || scannedValue.current.value !== floorProducts[indexProduct].upc)){
      playErrorSound()
      setIsScannedBin(false)
      setMessage("The value of bin or upc scanned is even empty or incorrect !");
      setShowMessageErr(true);
      setTimeout(() => {
        setShowMessageErr(false);
      }, 3000);
    } else {
      const userID = getUserIdFromLocalStorage();

      const obj = {
        label: hiddenProductId.current.value,
        quantity: floorProducts[indexProduct].quantity > 10 && biggerThanTen.current?.value ? parseInt(biggerThanTen.current.value) : 1,
        user_id: userID,
        scanned_stand: isScannedBin,
        ...(floorProducts[indexProduct].bin_id
          ? { bin_id: floorProducts[indexProduct].bin_id }
          : floorProducts[indexProduct].upc
          ? { upc: floorProducts[indexProduct].upc }
          : floorProducts[indexProduct].big_upc
          ? { big_upc: floorProducts[indexProduct].big_upc }
          : {})
      };
      if(floorProducts[indexProduct].type == 'order'){
        obj.order_id = floorProducts[indexProduct].order_id;
      }else{
        obj.return_order_id = floorProducts[indexProduct].order_id;
      }

      try {
        let url = '';
        if (floorProducts[indexProduct].type == 'order') {
          url = buildLink("pickupOrderProduct");
        }else{
          url = buildLink("pickupReturnOrderProduct");
        }
        const queryParams = new URLSearchParams(obj).toString();
        const res = await axiosServer.post(url + "&" + queryParams);
        
        // Optimistically update the UI using the scan response
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          const scanResult = res.data.data[0];
          if (scanResult && scanResult.order_product_id) {
            // Show success message and play success sound
            playSuccessSound();
            if (scanResult.pickedup === true) {
              setMessage("Product successfully picked up!");
            } else {
              setMessage(`Product scanned! Quantity: ${scanResult.counter}`);
            }
            setShowMessageSucc(true);
            setTimeout(() => {
              setShowMessageSucc(false);
            }, 3000);
            
            setFloorProducts(prev => {
              const updatedProducts = prev.map(prod => {
                if (prod.order_product_id === scanResult.order_product_id) {
                  return {
                    ...prod,
                    picked: scanResult.pickedup,
                    picked_qty: scanResult.counter,
                  };
                }
                return prod;
              });

              if (scanResult.pickedup === true) {
                setTimeout(() => {
                  navigateToNextProduct(updatedProducts, indexProduct);
                }, 1000);
              }

              return updatedProducts;
            });

            floorProducts.forEach((product) =>{
                if (product?.order_product_id === scanResult.order_product_id){
                  setLocalScannedCounts(prevCounts => ({
                  ...prevCounts,
                  [scanResult.order_product_id]:
                    (prevCounts[scanResult.order_product_id] || 0) + 1,
                }));
                }
              })


          }
        }
      } catch (e) {
        hiddenProductId.current.value = "";
        playErrorSound()
        setMessage(e.response.data.message);
        setShowMessageErr(true);
        setTimeout(() => {
          setShowMessageErr(false);
        }, 3000);
      }
    }
    hiddenProductId.current.value = "";
  }

  async function scanMissingProduct(e) {
    e.preventDefault();

    if(scannedValue.current.value == "" && (scannedValue.current.value !== floorProducts[indexProduct].bin_id || scannedValue.current.value !== floorProducts[indexProduct].upc)){
      playErrorSound()
      setIsScannedBin(false)
      setMessage("The value of bin or upc scanned is even empty or incorrect !");
      setShowMessageErr(true);
      setTimeout(() => {
        setShowMessageErr(false);
      }, 3000);
    } else {
      const userID = getUserIdFromLocalStorage();

      let labelValue;
      let combined;
      if(floorProducts[indexProduct].option.code){
        labelValue=floorProducts[indexProduct].option.code;
      }
      else{
        labelValue=floorProducts[indexProduct].code;
      }

      combined=floorProducts[indexProduct].combined_product;

      const obj = {
        label: labelValue,
        quantity: floorProducts[indexProduct].quantity > 10 ? biggerThanTen.current.value : floorProducts[indexProduct].quantity,
        user_id: userID,
        scanned_stand: isScannedBin,
        ...(combined ? { combined_product: combined } : {}),
        ...(floorProducts[indexProduct].bin_id
          ? { bin_id: floorProducts[indexProduct].bin_id }
          : floorProducts[indexProduct].upc
          ? { upc: floorProducts[indexProduct].upc }
          : floorProducts[indexProduct].big_upc
          ? { big_upc: floorProducts[indexProduct].big_upc }
          : {})
      };

      if(floorProducts[indexProduct].type == 'order'){
        obj.order_id = floorProducts[indexProduct].order_id;
      }else{
        obj.return_order_id = floorProducts[indexProduct].order_id;
      }

      try {
        let url = '';
        if (floorProducts[indexProduct].type == 'order') {
          url = buildLink("missingPickupOrderProduct");
        } else {
          url = buildLink("missingPickupReturnOrderProduct");
        }
        const queryParams = new URLSearchParams(obj).toString();
        const res = await axiosServer.post(url + "&" + queryParams);
        
        // Show success message and play success sound when missing item is submitted
        playSuccessSound();
        setMessage("The item has been submitted as missing.");
        setShowMessageSucc(true);
        setTimeout(() => {
          setShowMessageSucc(false);
        }, 3000);
        
        // Mark this product as missing_submitted in floorProducts and navigate
        setFloorProducts(prev => {
          const updatedProducts = prev.map((prod, idx) =>
            idx === indexProduct ? { ...prod, missing_submitted: true } : prod
          );
          
          // Navigate to the next unscanned product after marking as missing
          setTimeout(() => {
            navigateToNextProduct(updatedProducts, indexProduct);
          }, 100);
          
          return updatedProducts;
        });
      } catch (e) {
        playErrorSound();
        setNext(false);
        setMessage(e.response.data.message);
        setShowMessageErr(true);
        setTimeout(() => {
          setShowMessageErr(false);
        }, 3000);
      }
    }
    hiddenProductId.current.value = "";
  }

  useEffect(() => {
    const handleBinFocus = () => {
      setHasBinFocus(document.activeElement === scannedValue.current);
    };

    const handleBinBlur = () => {
      setHasBinFocus(false);
    };
    const handleProductFocus = () => {
      setHasproductFocus(document.activeElement === hiddenProductId.current);
    };

    const handleProductBlur = () => {
      setHasproductFocus(false);
    };

    // Capture current ref values
    const scannedValueElement = scannedValue.current;
    const hiddenProductIdElement = hiddenProductId.current;

    if (scannedValueElement) {
      scannedValueElement.addEventListener("focus", handleBinFocus);
      scannedValueElement.addEventListener("blur", handleBinBlur);
    }
    if (hiddenProductIdElement) {
      hiddenProductIdElement.addEventListener("focus", handleProductFocus);
      hiddenProductIdElement.addEventListener("blur", handleProductBlur);
    }

    return () => {
      if (scannedValueElement) {
        scannedValueElement.removeEventListener("focus", handleBinFocus);
        scannedValueElement.removeEventListener("blur", handleBinBlur);
      }
      if (hiddenProductIdElement) {
        hiddenProductIdElement.removeEventListener("focus", handleProductFocus);
        hiddenProductIdElement.removeEventListener("blur", handleProductBlur);
      }
    };
  }, []);

  // Manual navigation handlers
  const handlePreviousProduct = () => {
    if (indexProduct > 0) {
      setindexProduct(indexProduct - 1);
      setMissingButtonHidden(false);
    }
  };

  const handleNextProduct = () => {
    if (indexProduct < floorProducts.length - 1) {
      setindexProduct(indexProduct + 1);
      setMissingButtonHidden(false);
    }
  };

  const handleNextButtonClick = async () => {
    const currentProduct = floorProducts[indexProduct];
    if (isProductComplete(currentProduct)) {
      if (indexProduct !== floorProducts.length - 1) {
        // Try to find next unscanned, otherwise just go to next
        const nextUnscanned = findNextUnscannedProduct(floorProducts, indexProduct);
        if (nextUnscanned !== -1) {
          setindexProduct(nextUnscanned);
        } else {
          setindexProduct(indexProduct + 1);
        }
      } else {
        // On last product, runRequests again before finishing
        if (lastRequestInfoRef.current) {
          await runRequests(lastRequestInfoRef.current);
        }
        // Clear localStorage on finish
        localStorage.removeItem("pickupFloorRequestInfo");
        history(-1);
      }
    }
  };

  return (
    <>
      <form
        className=" fixed left-[900px]"
        onSubmit={(e) => {
          checkShelfLabel(e);
        }}
      >
        <input ref={scannedValue} />
      </form>

      <form
        className=" fixed left-[900px]"
        onSubmit={(e) => {
          scanProduct(e);
        }}
      >
        {" "}
        <input ref={hiddenProductId} />
      </form>

      {/* header */}
      <div className="  sticky top-0 flex flex-col bg-white z-50">
        <div className=" w-full flex flex-row justify-between  bg-dbase h-16">
          <div className=" container flex relative gap-7 flex-row justify-start w-full text-white  h-full my-auto">
            <button onClick={() => {history(-1); localStorage.removeItem("pickupFloorRequestInfo");}}>
              <FaArrowLeft />
            </button>
            <h2 className=" text-xl my-auto"> Scan PickUp Floor</h2>
          </div>
        </div>
      </div>
      <div className="w-full h-16 transition-all duration-300 relative overflow-hidden bg-white flex justify-center items-center text-xl font-bold gap-6 px-4">
        <span className="flex items-center gap-2 text-dgreen">
          <FaCheckCircle className="my-auto" />
          Picked: {productsPicked}
        </span>
        <div className="h-8 w-0.5 bg-dgreyBlack"></div>
        <span className="flex items-center gap-2 text-dbase">
          <TbCircleXFilled className="text-2xl my-auto" />
          Not picked: {productsNotPicked}
        </span>
      </div>
      {floorProducts && floorProducts.length > 0 && <>
        <div className={` relative flex flex-col  pb-28    gap-2`}>

        <button onClick={() => scannedValue.current.focus()} disabled={floorProducts[indexProduct].picked} className={` ${isScannedBin  || floorProducts[indexProduct]?.picked ? " bg-dgreen2" : "bg-white"}  text-dblackk flex flex-row  w-full justify-center  text-3xl   py-2  shadow-md `}> <> Scan {" "}<strong>{storeType}</strong></>
        {!floorProducts[indexProduct].picked && <span className={`relative ${hasbinFocus ? "opacity-100" : "opacity-0"} transition-all  flex h-3 w-3`}>
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-dbase opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3  bg-dbase1"></span>
          </span>}
        </button>

        {floorProducts[indexProduct].bin_id ?
          <div className=" w-full shadow-md shadow-dbluegray text-center  flex flex-row justify-between  py-3   ">
            <h2 className=" bg-white w-full  font-bold text-3xl ">{floorProducts[indexProduct].floor_name}</h2>
            <h2 className=" bg-white w-full  font-bold text-3xl ">{floorProducts[indexProduct].stand_name}</h2>
            <h2 style={{ backgroundColor: floorProducts[indexProduct].bin_color || 'white' }} className="w-full font-bold text-3xl">{floorProducts[indexProduct].bin_name}</h2>

          </div>
          :
          <div className=" w-full shadow-md shadow-dbluegray text-center  flex flex-row justify-between  py-3   ">
            <h2 className=" bg-white w-full  font-bold text-3xl ">{storeTypeVal}</h2>
          </div>
        }
        <div onClick={(e) => {
          if (!e.target.matches("input")) {
            hiddenProductId.current.focus()
          }
        }} className={`flex flex-row gap-2 mt-10 justify-start ${floorProducts[indexProduct].picked ? 'bg-[#abf7b1]' : ''} items-center container mx-auto p-4  `}
        disabled={floorProducts[indexProduct].picked}
        >

          <div className="  w-[300px] overflow-hidden relative">
            <img className=" relative object-cover w-full h-full" src={floorProducts[indexProduct].mobile_image} />

          </div>

          <div className=" flex justify-center flex-col gap-1">
            <div className=" justify-start text-left gap-3 flex flex-row ">
              <h2 className=" w-fit font-bold text-[0.9rem] lg:text-lg bg-dinputBorder py-0.5 px-4 rounded-sm">{floorProducts[indexProduct].sku}</h2>
              <h2 className=" w-fit font-bold text-[0.8rem] lg:text-lg  bg-dinputBorder py-0.5 px-4 rounded-sm">Total Qty : {floorProducts[indexProduct].total_quantity}</h2>
              {!floorProducts[indexProduct].picked && <span className={`relative ${hasproductFocus ? "opacity-100" : "opacity-0"} transition-all  flex h-3 w-3`}>
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-dbase opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3  bg-dbase1"></span>
              </span>}
            </div>
            <h2 className="">{floorProducts[indexProduct].name}</h2>
            {floorProducts[indexProduct].option?.name && <h2 className=" flex flex-row justify-start text-start gap-2"><strong>{floorProducts[indexProduct].option.name}</strong><span>{floorProducts[indexProduct].option.value}</span></h2>}
            <div className=" flex flex-row justify-start gap-4">
              <h2 className=" text-dgreen"><span>Qty</span> <strong>{floorProducts[indexProduct].quantity}</strong></h2>
              {floorProducts[indexProduct].picked ? <h2 className=" text-dgreen">Product Picked</h2> : <h2 className=" text-dbase"><span>Scanned Value</span> <strong>{counter}</strong></h2>}
            </div>
            {floorProducts[indexProduct].quantity > 10 && (
              <div className=" flex flex-row justify-start gap-2">
                <label>Qty</label>
                <input
                  type="number"
                  ref={biggerThanTen}
                  className="border w-16 border-dhotPink text-center rounded-lg  "

                />
              </div>
            )}

          </div>
        </div>
        <div className=" flex justify-between container  mt-10   py-2 ">
          <button disabled={indexProduct === 0} onClick={handlePreviousProduct} className={` rounded-full  ${indexProduct > 0 ? " bg-dbase" : "bg-dgrey1"} p-2 text-white `}>
            <FaFastBackward className=" text-black" />
          </button>
          {!missing && !floorProducts[indexProduct]?.missing_submitted && !floorProducts[indexProduct]?.picked && (
            <button disabled={disable} onClick={(e) => {
              scanMissingProduct(e);
            }} className={` rounded-full   bg-dbase p-2 text-white ${disable ? " bg-dgrey1" : "bg-dbase"} `}>
              Missing Item
            </button>
          )}
          <button disabled={indexProduct === floorProducts.length - 1} onClick={handleNextProduct} className={` rounded-full  ${indexProduct !== floorProducts.length - 1 ? " bg-dbase" : "bg-dgrey1"} p-2 text-white `}>
            <FaFastForward className="  text-black" />
          </button>
        </div>
      </div>
      {/* Loader below products only on finish (last product) */}
      {loading && indexProduct === floorProducts.length - 1 && (
        <div className="flex justify-center items-center py-20">
          <PointsLoader />
        </div>
      )}

        <button
          id="nextButton"
          onClick={handleNextButtonClick}
          className={`${(isProductComplete(floorProducts[indexProduct]))
            ? "bg-dbase text-white"
            : "bg-dplaceHolder text-dblack"
            } w-full py-3 font-semibold px-5 fixed bottom-0 left-0 right-0 flex flex-row gap-3 justify-center text-md mt-10`}
        >
          {indexProduct !== floorProducts.length - 1 ? (
            <>
              <div className="flex flex-row justify-between text-center w-full">
                <span>Next Product</span>
                <div className="flex flex-row justify-end gap-4">
                  {floorProducts[indexProduct + 1].address ? (
                    <>
                      Address: {floorProducts[indexProduct + 1].address}
                    </>
                  ) : (
                    <>
                      UPC: {floorProducts[indexProduct + 1].bin_id
                        ? floorProducts[indexProduct + 1].bin_id
                        : floorProducts[indexProduct + 1].upc
                          ? floorProducts[indexProduct + 1].upc
                          : floorProducts[indexProduct + 1].big_upc || "N/A"}
                    </>
                  )}
                  <FaArrowRight className="my-auto" />
                </div>
              </div>
            </>
          ) : (
            <span>Finish</span>
          )}
        </button>
      </>
      }
      {/* Loader at top for all other steps except finish */}
      {loading && indexProduct !== floorProducts.length - 1 && (
        <div className="flex justify-center items-center py-20">
          <PointsLoader />
        </div>
      )}

      <MessageAlert
        message={message}
        type={showMessgErr ? "err" : "succ"}
        showMessg={showMessgErr || (showMessgSucc && true)}
      />
    </>
  );
};

export default hocStarredPage(ScanPickUpFloor);